// A watchtower compound, described the way age-of-mcp authors it: a `shaft`
// helper, a `courtyard` helper and a battlement-merlon loop plus the shared
// `foundationSlab` and `scaffold`, walked through its build stages. Ported 1:1
// from age-of-mcp's tower.ts.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, climb, type Part } from "../../lib";

const STONE = "#878d92";
const TRIM  = "#6b7074";
const DARK  = "#1a1a1a";
const FLAG  = "#a83a3a";
const TAU   = Math.PI * 2;
const BASE  = 0.14;
const R     = 0.46;   // a slimmer shaft — the tower no longer fills the whole plot
const WH    = 0.34;   // courtyard wall height
const WR    = 0.86;   // courtyard wall radius (plot edge)

function shaft(h: number): Part {
  return { geometry: new THREE.CylinderGeometry(R, R, h, 8), color: STONE, position: [0, BASE + h / 2, 0] };
}

// The low crenellated courtyard wall ringing the plot, with an entrance gap on
// the front (+z, road) side and a few merlons.
function courtyard(): Part[] {
  const wy = BASE + WH / 2;
  const out: Part[] = [
    { geometry: new THREE.BoxGeometry(WR * 2 + 0.1, WH, 0.12), color: STONE, position: [0, wy, -WR] },   // back
    { geometry: new THREE.BoxGeometry(0.12, WH, WR * 2 + 0.1), color: STONE, position: [-WR, wy, 0] },   // left
    { geometry: new THREE.BoxGeometry(0.12, WH, WR * 2 + 0.1), color: STONE, position: [ WR, wy, 0] },   // right
    { geometry: new THREE.BoxGeometry(0.58, WH, 0.12), color: STONE, position: [-0.57, wy, WR] },        // front-left
    { geometry: new THREE.BoxGeometry(0.58, WH, 0.12), color: STONE, position: [ 0.57, wy, WR] },        // front-right (gap in the middle)
  ];
  // A few merlons along the back and side walls.
  const merlon = (x: number, z: number): Part =>
    ({ geometry: new THREE.BoxGeometry(0.14, 0.12, 0.14), color: TRIM, position: [x, BASE + WH + 0.06, z] });
  for (const x of [-0.6, 0, 0.6]) out.push(merlon(x, -WR));
  for (const z of [-0.5, 0.1]) { out.push(merlon(-WR, z)); out.push(merlon(WR, z)); }
  return out;
}

// The build stages of the tower: foundation, shaft with scaffolding, taller
// shaft, and the finished tower with courtyard, battlement merlons, door,
// arrow-slits, banner and brazier.
function partsFor(s: number): Part[] {
  if (s <= 0) return [foundationSlab(1.84)];
  if (s === 1) return [foundationSlab(1.84), shaft(0.8), ...scaffold(1.0, 1.0)];
  if (s === 2) return [foundationSlab(1.84), shaft(1.6), ...scaffold(1.0, 1.85)];

  const h   = 2.5;
  const top = BASE + h;
  const parts: Part[] = [foundationSlab(1.84), shaft(h), ...courtyard()];
  // Battlement merlons ringing the shaft top.
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * TAU;
    parts.push({
      geometry: new THREE.BoxGeometry(0.2, 0.26, 0.2),
      color: TRIM,
      position: [Math.cos(a) * R, top + 0.13, Math.sin(a) * R],
      rotation: [0, -a, 0],
    });
  }
  parts.push(
    // Door at the base + arrow-slit windows up the shaft.
    { geometry: new THREE.BoxGeometry(0.3, 0.58, 0.08), color: "#4a2c18", position: [0, BASE + 0.29, R] },
    { geometry: new THREE.BoxGeometry(0.08, 0.3, 0.08), color: DARK, position: [0, BASE + 1.5, R] },
    { geometry: new THREE.BoxGeometry(0.08, 0.3, 0.08), color: DARK, position: [0, BASE + 1.0, R] },
    // Banner pole + flag on top.
    { geometry: new THREE.CylinderGeometry(0.03, 0.03, 0.7, 4), color: "#3a2412", position: [0, top + 0.46, 0] },
    { geometry: new THREE.BoxGeometry(0.44, 0.26, 0.04), color: FLAG, position: [0.22, top + 0.68, 0] },
    // A brazier in a courtyard corner: a stand, a bowl, and a flame.
    { geometry: new THREE.CylinderGeometry(0.04, 0.06, 0.3, 6), color: DARK, position: [0.62, BASE + 0.15, 0.55] },
    { geometry: new THREE.CylinderGeometry(0.11, 0.07, 0.1, 8), color: "#3a3a3a", position: [0.62, BASE + 0.33, 0.55] },
    { geometry: new THREE.ConeGeometry(0.08, 0.22, 6), color: "#f08a2c", position: [0.62, BASE + 0.46, 0.55] },
  );
  return parts;
}

export const tower = defineModel({
  id: "tower",
  tiles: { w: 2, h: 2 },
  batchId: "standard",
  name: "Tower",
  tags: ["building","tower","low-poly","age-of-mcp","classic","military"],
  summary: "Low-poly tower.",
  description: "A tall stone watchtower with a crenellated top, trimmed with gold and gems — a grand prestige landmark.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
  stations: [
    { id: "face", position: [0, 0.14, 0.62], facing: Math.PI, description: "The tower face a sentry climbs." },
  ],
  interactions: [
    { id: "climb", label: "Climbing the tower", station: "face", variants: ["complete"], ...climb() },
  ],
});
